export type ProfileLanguagePreferenceRow = {
    preferred_language_tag: string | null;
};

type QueryResult = {
    data: ProfileLanguagePreferenceRow | null;
    error: unknown | null;
};

type PreferenceFilter = {
    eq(column: string, value: string): PreferenceFilter;
    is(column: string, value: null): PreferenceFilter;
    select(columns: 'preferred_language_tag'): {
        maybeSingle(): Promise<QueryResult>;
    };
};

export type ProfileLanguagePreferenceClient = {
    from(table: 'profiles'): {
        update(payload: { preferred_language_tag: string }): PreferenceFilter;
    };
};

export type CompareAndSetLanguageResult =
    | { status: 'updated'; preferredLanguageTag: string }
    | { status: 'conflict' };

export type AuthLanguageMetadata = {
    paperzilla_auth_message_locale: string;
    preferred_language_tag: string | null;
    paperzilla_product_language_authority: string | null;
};

export type AuthLanguageMetadataWriteResult =
    | { status: 'written' }
    | { status: 'superseded' }
    | { status: 'failed'; error: unknown };

type AuthLanguageMetadataWriter = (
    userId: string,
    metadata: AuthLanguageMetadata
) => Promise<void>;

/**
 * Serialize Auth metadata writes and discard queued stale generations. If a
 * newer preference arrives while an older request is in flight, the newer
 * write always runs afterward, so reverse network completion cannot restore
 * the older language.
 */
export function createAuthLanguageMetadataWriteQueue({
    write,
    isCurrentUser = () => true,
}: {
    write: AuthLanguageMetadataWriter;
    isCurrentUser?: (userId: string) => boolean;
}) {
    let latestGeneration = 0;
    let tail: Promise<void> = Promise.resolve();

    return {
        enqueue(
            userId: string,
            metadata: AuthLanguageMetadata
        ): Promise<AuthLanguageMetadataWriteResult> {
            const generation = ++latestGeneration;
            const immutableMetadata = { ...metadata };
            const operation = tail.then(async (): Promise<AuthLanguageMetadataWriteResult> => {
                if (generation !== latestGeneration || !isCurrentUser(userId)) {
                    return { status: 'superseded' };
                }
                try {
                    await write(userId, immutableMetadata);
                } catch (error) {
                    return { status: 'failed', error };
                }
                return generation === latestGeneration
                    ? { status: 'written' }
                    : { status: 'superseded' };
            });
            tail = operation.then(() => undefined, () => undefined);
            return operation;
        },
    };
}

/**
 * Atomically change the preference only if the row still contains the value
 * that the UI rendered. A zero-row update is a conflict, never a success.
 */
export async function compareAndSetProfileLanguagePreference(
    client: ProfileLanguagePreferenceClient,
    userId: string,
    expectedLanguageTag: string | null,
    nextLanguageTag: string
): Promise<CompareAndSetLanguageResult> {
    if (!userId.trim() || !nextLanguageTag.trim()) {
        throw new Error('Profile language compare-and-set requires a user and language tag');
    }

    let query = client
        .from('profiles')
        .update({ preferred_language_tag: nextLanguageTag })
        .eq('id', userId);
    query = expectedLanguageTag === null
        ? query.is('preferred_language_tag', null)
        : query.eq('preferred_language_tag', expectedLanguageTag);
    const { data, error } = await query
        .select('preferred_language_tag')
        .maybeSingle();

    if (error) {
        throw error;
    }
    if (!data) {
        return { status: 'conflict' };
    }
    if (data.preferred_language_tag !== nextLanguageTag) {
        throw new Error('Profile language compare-and-set returned an unexpected value');
    }
    return { status: 'updated', preferredLanguageTag: nextLanguageTag };
}
